import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Phone } from 'lucide-react';
import { Place } from '../../../types/results.types';

interface PhoneContactCellProps {
  place: Place;
  compact?: boolean;
}

const getPhoneNumbers = (place: Place): string[] => {
  const candidates = place.phone_numbers?.length ? place.phone_numbers : [place.phone_number];
  return Array.from(new Set(candidates.map((phone) => phone?.trim()).filter(Boolean) as string[]));
};

const toPhoneHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

const PhoneContactCell: React.FC<PhoneContactCellProps> = ({ place, compact = false }) => {
  const phones = getPhoneNumbers(place);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
  }, []);

  if (phones.length === 0) {
    return <span className="text-[12px] font-medium text-[#9CA3AF]">No phone listed</span>;
  }

  const primaryPhone = phones[0];

  const copyPhone = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(primaryPhone);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={`flex min-w-0 items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
      <Phone className="h-3.5 w-3.5 flex-shrink-0 text-[#8A3033]" />
      <a
        href={toPhoneHref(primaryPhone)}
        onClick={(event) => event.stopPropagation()}
        className="truncate text-[13px] font-semibold text-[#2D3142] transition-colors hover:text-[#8A3033]"
      >
        {primaryPhone}
      </a>
      {phones.length > 1 && (
        <span className="flex-shrink-0 text-[10.5px] font-medium text-[#6B7280]">+{phones.length - 1}</span>
      )}
      <button
        type="button"
        onClick={copyPhone}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-[#8B8580] transition-colors hover:bg-[#F6F3EF] hover:text-[#8A3033] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#8A3033]"
        aria-label={copied ? 'Phone number copied' : `Copy ${primaryPhone}`}
        title={copied ? 'Copied' : 'Copy phone number'}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-[#0F6E56]" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

export default PhoneContactCell;
